import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Link2, RefreshCw, WifiOff } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { wmMessage } from "@/lib/wmError";

const C = OPS.wm.admin;

/** Why a record would be saved without a cost (SPEC §2.4; `needs_factor` is
 *  a weighed product linked without its POS pieces per kilo). */
const GROUPS = [
    { key: "unlinked", label: "wm_missing_unlinked", hint: "wm_missing_unlinked_hint", links: true },
    { key: "dangling", label: "wm_missing_dangling", hint: "wm_missing_dangling_hint", links: true },
    { key: "needs_factor", label: "wm_missing_needs_factor", hint: "wm_missing_needs_factor_hint", links: true },
    { key: "zero_cost_lines", label: "wm_missing_zero_lines", hint: "wm_missing_zero_lines_hint" },
    { key: "stock_without_cost", label: "wm_missing_stock", hint: "wm_missing_stock_hint" },
];
const CAUSE_KEY = {
    sc_item_missing: "wm_missing_cause_sc_item_missing",
    unit_mismatch: "wm_missing_cause_unit_mismatch",
    no_cost: "wm_missing_cause_no_cost",
};

const nameOf = (o, lang) => (lang === "ar" ? o?.name_ar || o?.name_en : o?.name_en || o?.name_ar) || "";

/** A recipe line's ingredient: a name, or `{ingredient, ingredient_key}`. */
const ingredientOf = (x) => {
    const v = x?.ingredient ?? x?.ingredient_name ?? x?.ingredient_key;
    if (v && typeof v === "object") return v.ingredient || v.ingredient_key || "";
    return v || "";
};

function Line({ g, x, i }) {
    const { t, lang } = useI18n();
    if (g === "zero_cost_lines") {
        const rid = `${x.recipe_key || ""}:${ingredientOf(x)}:${i}`;
        const codes = Array.isArray(x.linked_items) ? x.linked_items.filter(Boolean) : [];
        return (
            <li data-testid={C.missingRow(g, rid)} className="py-2">
                <bdi className="block text-sm font-semibold text-[color:var(--ink)]">{x.recipe_name || x.recipe_key}</bdi>
                <bdi className="block text-[11px] text-[color:var(--ink-muted)]">{ingredientOf(x)}</bdi>
                {codes.length ? (
                    <span className="block text-[10px] text-[color:var(--ink-faint)]">
                        {t("wm_missing_linked_items")}: <bdi dir="ltr">{codes.join(" · ")}</bdi>
                    </span>
                ) : null}
            </li>
        );
    }
    const it = x?.item || x;
    return (
        <li data-testid={C.missingRow(g, it?.id || i)} className="flex items-baseline gap-2 py-2">
            {it?.code ? (
                <bdi dir="ltr" className="shrink-0 text-[11px] tabular-nums text-[color:var(--ink-faint)]">{it.code}</bdi>
            ) : null}
            <span className="min-w-0">
                <span className="block text-sm font-semibold text-[color:var(--ink)]">{nameOf(it, lang)}</span>
                {g === "dangling" && (x.recipe_key || it?.recipe_key) ? (
                    <bdi className="block text-[11px] text-[color:var(--danger)]">{x.recipe_key || it.recipe_key}</bdi>
                ) : null}
                {g === "stock_without_cost" && (it?.sc_item_code || x.cause) ? (
                    <span className="block text-[11px] text-[color:var(--ink-muted)]">
                        {it?.sc_item_code ? <bdi dir="ltr">{it.sc_item_code}</bdi> : null}
                        {it?.sc_item_code && CAUSE_KEY[x.cause] ? " · " : null}
                        {CAUSE_KEY[x.cause] ? t(CAUSE_KEY[x.cause]) : null}
                    </span>
                ) : null}
            </span>
        </li>
    );
}

/**
 * «تكلفة ناقصة» (D5) — everything that would leave a record without a cost:
 * items not linked to a recipe, links to a recipe the POS no longer has,
 * recipe lines costed at zero, and raw items without a stock-count cost.
 * Information only: a missing cost never blocks a save.
 */
export function MissingCostAdmin({ onOpenLinks }) {
    const { t, lang } = useI18n();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setFailed(false);
        try {
            const r = await api.get("/waste-meals/admin/missing-cost");
            setData(r.data || {});
        } catch (e) {
            const msg = wmMessage(e, lang);
            if (msg) toast.error(msg); else setFailed(true);
        } finally {
            setLoading(false);
        }
    }, [lang]);

    useEffect(() => { load(); }, [load]);

    const num = (v) => new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US").format(v || 0);
    const listOf = (k) => (Array.isArray(data?.[k]) ? data[k] : []);
    const total = GROUPS.reduce((a, g) => a + listOf(g.key).length, 0);

    return (
        <div data-testid={C.missingCost} className="space-y-3">
            <div className="flex items-start gap-2">
                <p className="flex-1 text-xs leading-relaxed text-[color:var(--ink-muted)]">{t("wm_missing_hint")}</p>
                <Button type="button" variant="ghost" size="sm" onClick={load} disabled={loading}
                        aria-label={t("wm_links_refresh")} className="h-9 w-9 shrink-0 p-0">
                    <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                </Button>
            </div>

            {failed ? (
                <div data-testid={OPS.wm.offlineError}
                     className="flex items-center justify-between gap-2 rounded-[var(--radius-sm)] bg-[color:rgba(220,38,38,0.08)] px-3 py-2">
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-[color:var(--danger)]">
                        <WifiOff className="h-3.5 w-3.5" />{t("wm_offline_error")}
                    </span>
                    <Button size="sm" variant="outline" data-testid={OPS.wm.retry} onClick={load}>{t("wm_retry")}</Button>
                </div>
            ) : null}

            {loading && !data ? <InlineLoader /> : data && !total ? (
                <p data-testid={C.missingAllClear}
                   className="flex items-center justify-center gap-2 rounded-[var(--radius-lg)] border border-dashed border-[color:var(--border-soft)] py-10 text-center text-sm font-semibold text-[color:var(--success)]">
                    <CheckCircle2 className="h-4 w-4" />{t("wm_missing_all_clear")}
                </p>
            ) : data ? (
                <div className={`grid gap-3 transition-opacity duration-200 lg:grid-cols-2 ${loading ? "opacity-60" : ""}`}>
                    {GROUPS.map((g) => {
                        const list = listOf(g.key);
                        return (
                            <section key={g.key} data-testid={C.missingGroup(g.key)} data-count={list.length}
                                     className="rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3">
                                <header className="flex items-center gap-2">
                                    {list.length
                                        ? <AlertTriangle className="h-4 w-4 shrink-0 text-[color:var(--warning-soft-fg)]" />
                                        : <CheckCircle2 className="h-4 w-4 shrink-0 text-[color:var(--success)]" />}
                                    <h3 className="flex-1 text-sm font-bold text-[color:var(--ink)]">{t(g.label)}</h3>
                                    <bdi dir="ltr" className="text-sm font-extrabold tabular-nums text-[color:var(--ink)]">{num(list.length)}</bdi>
                                </header>
                                <p className="mt-0.5 text-[11px] leading-relaxed text-[color:var(--ink-faint)]">
                                    {t(g.hint)}
                                    {g.key === "stock_without_cost" && data?.period ? <> · <bdi dir="ltr">{data.period}</bdi></> : null}
                                </p>
                                {list.length ? (
                                    <ul className="mt-1 max-h-72 divide-y divide-[color:var(--separator)] overflow-y-auto scrollbar-thin">
                                        {list.map((x, i) => <Line key={i} g={g.key} x={x} i={i} />)}
                                    </ul>
                                ) : (
                                    <p className="mt-2 text-xs text-[color:var(--ink-muted)]">{t("wm_missing_empty_group")}</p>
                                )}
                                {g.links && list.length && onOpenLinks ? (
                                    <Button type="button" variant="outline" size="sm" onClick={onOpenLinks}
                                            className="mt-2 h-9 gap-1.5 text-xs font-semibold">
                                        <Link2 className="h-4 w-4" />{t("wm_missing_open_links")}
                                    </Button>
                                ) : null}
                            </section>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}
